/**
 * In-app Methodology & Calculations page. A plain-English but accurate
 * description of how the calc engine estimates fiscal and economic impact,
 * including BEA RIMS II multiplier use. Mirrors the branded PDF
 * (public/develio-methodology.pdf). Written for EDC directors and their boards.
 */

import type { ReactNode } from 'react';

const PDF_URL = '/develio-methodology.pdf';

function Formula({ children }: { children: ReactNode }) {
  return (
    <pre className="my-3 overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 font-mono text-xs leading-relaxed text-sky-200">
      {children}
    </pre>
  );
}

function Example({ children }: { children: ReactNode }) {
  return (
    <div className="my-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.1em] text-develio-green">
        Worked example
      </div>
      {children}
    </div>
  );
}

function Section({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-teal-600 text-sm font-bold text-white">
          {n}
        </div>
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="space-y-2 text-sm text-slate-600">{children}</div>
    </section>
  );
}

export function HelpMethodologyRoute() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-16">
      <header className="flex flex-col gap-4 rounded-xl border border-brand-teal-200 bg-gradient-to-br from-brand-teal-50 to-white p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal-700">
            Methodology
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            How Develio calculates fiscal and economic impact
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            The approach, the calculations, and how we use federal BEA RIMS II data. Written so you
            can stand behind every number in front of a board.
          </p>
        </div>
        <a
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-lg bg-brand-teal-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-brand-teal-700"
        >
          Download methodology PDF
        </a>
      </header>

      <Section n={1} title="Our approach">
        <p>
          Develio estimates what a development project does to your community&rsquo;s economy and
          your local government&rsquo;s budget, over a 10-year horizon, using federal data and
          transparent assumptions.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Federally grounded.</strong> Regional ripple effects come from the U.S. Bureau
            of Economic Analysis (BEA) RIMS II system, the same multipliers used in professional
            impact studies.
          </li>
          <li>
            <strong>Every assumption is an input.</strong> Nothing is hard-coded. Tax rates, service
            costs, capture rates, and growth rates are all values you can see and change.
          </li>
          <li>
            <strong>Conservative by default.</strong> Sales are reduced for displacement, equipment
            depreciates, only a share of workers relocate, and future dollars are discounted.
          </li>
        </ul>
      </Section>

      <Section n={2} title="The building blocks: direct, indirect, induced">
        <p>Every impact study separates a project&rsquo;s effect into three layers:</p>
        <div className="flex items-stretch gap-2">
          {[
            { t: 'Direct', d: "The company's own jobs, payroll, and spending" },
            { t: 'Indirect', d: 'Its local suppliers and their jobs' },
            { t: 'Induced', d: 'Spending by all those workers locally' },
          ].map((b, i) => (
            <div key={b.t} className="flex flex-1 items-center gap-2">
              <div className="flex-1 rounded-lg border border-slate-200 bg-white p-3 text-center">
                <div className="text-sm font-bold text-slate-900">{b.t}</div>
                <div className="mt-1 text-xs text-slate-500">{b.d}</div>
              </div>
              {i < 2 && <div className="text-lg font-extrabold text-develio-green">&rarr;</div>}
            </div>
          ))}
        </div>
        <p>
          A multiplier captures the jump from direct to total. A multiplier of 1.8 means every
          direct job supports 0.8 additional jobs elsewhere in your region.
        </p>
      </Section>

      <Section n={3} title="The federal data: BEA RIMS II multipliers">
        <p>
          RIMS II (Regional Input-Output Modeling System), published by the BEA, provides
          region-specific and industry-specific multipliers. Develio uses two kinds, for two
          purposes:
        </p>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="px-3 py-2 text-left font-semibold">Multiplier type</th>
                <th className="px-3 py-2 text-left font-semibold">Used for</th>
                <th className="px-3 py-2 text-left font-semibold">Applies to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-3 py-2 font-medium text-slate-800">Final-demand</td>
                <td className="px-3 py-2 text-slate-600">The construction phase</td>
                <td className="px-3 py-2 text-slate-600">A dollar amount (local spending)</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-medium text-slate-800">Direct-effect</td>
                <td className="px-3 py-2 text-slate-600">Ongoing operations</td>
                <td className="px-3 py-2 text-slate-600">A job count or payroll amount</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50 px-4 py-3 text-sm text-slate-600">
          These two are never mixed. Construction multipliers turn dollars into dollars and jobs.
          Operations multipliers turn the company&rsquo;s direct jobs and payroll into total jobs
          and payroll.
        </div>
        <p>
          <strong>Picking the right multiplier:</strong> you give the project&rsquo;s industry (a
          6-digit NAICS code); Develio matches it to the correct BEA industry and scores the match
          for confidence. Low-confidence matches are not used silently &mdash; Develio asks you to
          confirm, so the report always names the exact BEA industry used.
        </p>
        <p>
          <strong>Regional vs national:</strong> multipliers come by region (county, metro, combined
          area, or state). On the trial, Develio uses national/state averages so you can run an
          analysis immediately; for real reports, you or our team load the BEA workbook for your
          specific region and every analysis uses it automatically.
        </p>
      </Section>

      <Section n={4} title="The construction phase (one-time)">
        <p>
          Building the project is a temporary boost, measured separately because the jobs are
          short-term and the tax sources differ. Only spending that stays in your region counts, and
          the BEA final-demand multipliers apply to it:
        </p>
        <Formula>
          {`local spending  = total construction spending x local capture rate
output impact   = local spending x construction output multiplier
construction jobs = (local spending / $1M) x jobs-per-million multiplier`}
        </Formula>
        <Example>
          A $50M factory build, 70% local ($35M), output multiplier 1.8: output impact = $35M x 1.8
          = <strong>$63M</strong> of regional activity, and about <strong>1,575 job-years</strong>{' '}
          of construction work. Construction also generates one-time tax from materials, worker
          spending, and permits.
        </Example>
      </Section>

      <Section n={5} title="Ongoing operations (10 years)">
        <p>Once open, the project is modeled year by year using direct-effect multipliers:</p>
        <Formula>
          {`total jobs    = direct jobs x operations employment multiplier
total payroll = direct payroll x operations earnings multiplier`}
        </Formula>
        <Example>
          100 direct jobs at a 1.8 multiplier support <strong>180 total jobs</strong> (80 indirect
          and induced). $5M direct payroll at 1.5 supports <strong>$7.5M</strong> in total regional
          wages.
        </Example>
        <p>
          Each year is adjusted for a realistic ramp-up over the first three years, then for growth
          and depreciation: equipment depreciates, real property appreciates, sales grow at your
          assumed rate.
        </p>
      </Section>

      <Section n={6} title="Tax revenue, by jurisdiction">
        <p>
          Revenue is calculated separately for each taxing entity. The <strong>city</strong> can
          collect up to nine streams each year: sales tax on project sales (less displacement),
          sales tax on worker household spending, real property tax, business personal property tax,
          inventory tax, hotel occupancy tax, mixed beverage tax, utility franchise fees, and
          miscellaneous fees.
        </p>
        <p>
          The <strong>county</strong>, <strong>school district</strong>, and{' '}
          <strong>special districts</strong> levy property tax only, at their own rates, with their
          own per-worker or per-student costs. Property tax is assessed value times the rate (stored
          as a precise decimal, e.g. 0.00675 for a 0.675% effective rate).
        </p>
        <p>
          Against revenue, the model subtracts the cost of government services (per worker,
          inflating each year) and the cost of any incentive, such as a property tax abatement
          counted as foregone revenue for the years it applies.
        </p>
      </Section>

      <Section n={7} title="Net impact, return, and payback">
        <Formula>
          {`annual net benefit = total revenue - (service cost + incentive cost)
present value      = annual net benefit / (1 + discount rate)^year
return on incentive = 10-year gross fiscal benefit / total incentive cost`}
        </Formula>
        <Example>
          $12M of 10-year gross benefits against a $3M incentive is a <strong>4.0x return</strong>.
        </Example>
        <div className="rounded-lg border-l-4 border-develio-green bg-green-50 px-4 py-3 text-sm text-slate-600">
          For honesty, the return is measured against <strong>gross</strong> benefits (before the
          abatement is subtracted), because the abatement is already counted once as the incentive
          cost. This avoids double-counting and keeps the ratio defensible.
        </div>
        <p>
          <strong>Payback period</strong> is the point where cumulative benefits exceed the total
          incentive, to the fractional year. If they never catch up within 10 years, the report says
          so rather than implying a payback that does not occur.
        </p>
      </Section>

      <Section n={8} title="Testing the numbers">
        <p>
          <strong>Sensitivity analysis</strong> varies each key assumption up and down and re-runs
          the model, ranking which assumptions move the result the most.
        </p>
        <p>
          <strong>Monte Carlo simulation</strong> runs the model a thousand times with random
          variation in the most uncertain inputs and reports a range, not a single number.
        </p>
        <Example>
          &ldquo;The 10-year net benefit is <strong>$8.2M</strong> at the midpoint, with a 90%
          confidence range of <strong>$5.1M to $11.8M</strong>.&rdquo; A range like this is far more
          defensible to a board than a single point estimate.
        </Example>
      </Section>

      <Section n={9} title="Where every assumption comes from, and data sources">
        <p>
          Each input has a source and is overridable per project: your{' '}
          <strong>jurisdiction profile</strong> (tax rates, service costs), the{' '}
          <strong>BEA RIMS II dataset</strong> (multipliers), the <strong>project inputs</strong>{' '}
          (investment, jobs, payroll, sales), <strong>behavioral assumptions</strong> (capture,
          displacement, relocation), and your <strong>incentive package</strong>.
        </p>
        <p>
          Regional multipliers come from BEA RIMS II; the market intelligence profiles come from the
          U.S. Census Bureau and Bureau of Labor Statistics. Develio produces a defensible,
          transparent estimate, not a guarantee. Its value is that every figure traces to a federal
          source or a stated assumption you control.
        </p>
      </Section>

      <p className="text-center text-sm text-slate-400">
        Want the full version?{' '}
        <a
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-teal-700 underline"
        >
          Download the methodology PDF
        </a>
        .
      </p>
    </div>
  );
}
